import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';
import SEO from '../components/SEO';

export default function TreatmentsOverview() {
  const { lang, getLoc } = useLanguage();
  const t = translations[lang].treatmentsOverview;
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTreatments() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/treatments');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setTreatments(data);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Fallback
      }

      // Hardcoded fallback list if CMS API unreachable
      setTreatments([
        {
          id: '1',
          slug: { da: 'dybdevaevsmassage', en: 'deep-tissue-massage' },
          treatment_slug: { da: 'deep-tissue-massage', en: 'deep-tissue-massage' },
          title: { da: 'Bindevævsmassage', en: 'Deep Tissue Massage' },
          short_description: {
            da: 'Løsner dybsiddende spændinger, myoser og stivhed i muskler og led.',
            en: 'Releases deep-seated muscle tension, knots, and joint stiffness.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/deep-tissue.jpg'],
          duration: 60,
          price: 700
        },
        {
          id: '2',
          slug: { da: 'neuromuskulaer-massage', en: 'neuromuscular-massage' },
          treatment_slug: { da: 'neuromuscular-massage', en: 'neuromuscular-massage' },
          title: { da: 'Neuromuskulær massage', en: 'Neuromuscular Massage' },
          short_description: {
            da: 'Specialiseret behandling rettet mod nervesystem, triggerpunkter og kroniske muskelspændinger.',
            en: 'Specialized therapy targeting the nervous system, trigger points, and chronic muscular tension.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/neuromuscular-therapy-treatment.jpg'],
          duration: 60,
          price: 700
        },
        {
          id: '3',
          slug: { da: 'biodynamisk-kranio-sakral-terapi', en: 'bio-dynamic-craniosacral-therapy' },
          treatment_slug: { da: 'bio-dynamic-craniosacral-therapy', en: 'bio-dynamic-craniosacral-therapy' },
          title: { da: 'Biodynamisk Kranio-Sakral Terapi', en: 'Bio Dynamic Craniosacral Therapy' },
          short_description: {
            da: 'Ekstremt blid manuel terapi, der beroliger nervesystemet og skaber indre ro.',
            en: 'Extremely gentle manual therapy that calms the nervous system and creates inner peace.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/craniosacral.jpg'],
          duration: 60,
          price: 700
        },
        {
          id: '4',
          slug: { da: 'gravidmassage', en: 'pregnancy-massage' },
          treatment_slug: { da: 'pregnancy-massage', en: 'pregnancy-massage' },
          title: { da: 'Gravidmassage', en: 'Pregnancy Massage' },
          short_description: {
            da: 'Nænsom og støttende massage tilpasset den gravides krop og behov.',
            en: 'Nurturing massage tailored to the pregnant body and individual needs.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/pregnancy-massage.jpg'],
          duration: 60,
          price: 700
        },
        {
          id: '5',
          slug: { da: 'efterfoedselsmassage', en: 'post-partum-massage' },
          treatment_slug: { da: 'post-partum-massage', en: 'post-partum-massage' },
          title: { da: 'Efterfødselsmassage', en: 'Post Partum Massage' },
          short_description: {
            da: 'Gendannende efterfødselsmassage, der støtter kroppens heling og giver fornyet energi.',
            en: 'Restorative postpartum massage supporting healing and renewed energy for new mothers.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09348_2.jpg'],
          duration: 75,
          price: 1050
        },
        {
          id: '6',
          slug: { da: 'sportsmassage', en: 'sports-massage' },
          treatment_slug: { da: 'sports-massage', en: 'sports-massage' },
          title: { da: 'Sportsmassage', en: 'Sports Massage' },
          short_description: {
            da: 'Effektiv massage til motionister og atleter for hurtigere restitution og forebyggelse.',
            en: 'Effective massage for active individuals targeting muscle recovery and injury prevention.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/sports-massage.jpg'],
          duration: 60,
          price: 700
        },
        {
          id: '7',
          slug: { da: 'velvaeremassage', en: 'wellness-massage' },
          treatment_slug: { da: 'wellness-massage', en: 'wellness-massage' },
          title: { da: 'Velværemassage', en: 'Wellness Massage' },
          short_description: {
            da: 'Rolig og dybt afspændende helkropsmassage, der beroliger krop og sind.',
            en: 'Calm and deeply relaxing full-body massage that soothes body and mind.'
          },
          image: ['https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/wellness-massage.jpg'],
          duration: 60,
          price: 700
        }
      ]);
      setLoading(false);
    }

    fetchTreatments();
  }, []);

  const getSlugStr = (item) => {
    if (!item) return '';
    if (typeof item.treatment_slug === 'object') return item.treatment_slug[lang] || item.treatment_slug.da || item.treatment_slug.en || '';
    if (typeof item.slug === 'object') return item.slug[lang] || item.slug.da || item.slug.en || '';
    if (typeof item.treatment_slug === 'string') return item.treatment_slug;
    if (typeof item.slug === 'string') return item.slug;
    return 'deep-tissue-massage';
  };

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Behandlinger' : 'Treatments Overview'}
        description={lang === 'da'
          ? 'Oversigt over professionelle behandlinger hos Jette: Bindevævsmassage, Neuromuskulær massage, Kranio-Sakral Terapi, Gravidmassage, Efterfødselsmassage og Sportsmassage.'
          : 'Overview of professional treatments with Jette: Deep Tissue Massage, Neuromuscular Massage, Craniosacral Therapy, Pregnancy Massage, Post Partum Massage, and Sports Massage.'
        }
      />
      
      {/* Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            {t.tag}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760] leading-relaxed">
            {t.subtitle}
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#E8E0D7] font-semibold text-[#2E3191]">
              <i className="fa-solid fa-clock" /> {lang === 'da' ? 'Alle behandlinger er baseret på tid (60 – 120 min)' : 'All treatments are time-based (60 – 120 min)'}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EEF0F8] border border-[#2E3191]/20 font-semibold text-[#2E3191]">
              <i className="fa-solid fa-certificate" /> {lang === 'da' ? 'RAB godkendt - Anerkendt af sundhedsvæsenet' : 'RAB Approved Therapist'}
            </span>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {loading ? (
            <div className="py-12 text-center text-[#6E6760]">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-[#2E3191]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {treatments.map((item) => {
                const imgUrl = Array.isArray(item.image) && item.image[0] ? item.image[0] : 'https://assets.auxodev.dk/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/deep-tissue.jpg';
                const targetSlug = getSlugStr(item);

                return (
                  <div
                    key={item.id || targetSlug}
                    className="bg-[#FAF7F2] rounded-2xl border border-[#E8E0D7] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
                  >
                    <div>
                      <div className="aspect-16/10 overflow-hidden bg-[#E8E0D7]">
                        <img
                          src={imgUrl}
                          alt={getLoc(item.title)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6 space-y-3">
                        <div className="flex items-center justify-between text-xs font-semibold text-[#2E3191]">
                          <span><i className="fa-regular fa-clock mr-1" /> 60 – 120 min</span>
                          <span>{lang === 'da' ? 'Fra 700 DKK' : 'From 700 DKK'}</span>
                        </div>
                        <h2 className="font-serif-display text-2xl font-bold text-[#2C2825]">
                          {getLoc(item.title)}
                        </h2>
                        <p className="text-sm text-[#6E6760] leading-relaxed">
                          {getLoc(item.short_description)}
                        </p>
                      </div>
                    </div>

                    <div className="p-6 pt-0">
                      <Link
                        to={`/behandlinger/${targetSlug}`}
                        className="w-full py-3 rounded-xl bg-[#2E3191] text-white text-xs font-semibold hover:bg-[#1E2063] transition-all block text-center"
                      >
                        {t.readMore} →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </section>

    </div>
  );
}
